import React, { useMemo } from 'react';
import { GridFilterInputValueProps } from '@mui/x-data-grid-pro';
import { IntlShape, useIntl } from 'react-intl';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { TranslationKey } from 'intl/types/translationKey';
import { LabelledEntity } from 'shared/types/commonView';
import { MemoMultiAutocomplete } from 'components/common/simpleInputFields/multichoice/multiAutocomplete';
import { useEntityFilterInput } from './useEntityFilterInput';

interface EntityFilterConfig<T> {
  useData: () => T;
  transformData: (data: T, intl: IntlShape) => LabelledEntity[];
  labelId: TranslationKey;
  id: string;
  groupBy?: (option: LabelledEntity) => string;
}

const entityFilterInputCache = new Map<
  string,
  (props: GridFilterInputValueProps) => React.ReactElement
>();

/**
 * Factory for DataGrid entity filter input component.
 *
 * Prefer module-scope usage (outside React component render), e.g.:
 * `export const SiteFilterInput = createEntityFilterInput(config)`.
 *
 * Recreating the component type inside render can cause remounts
 * (state/focus loss) instead of normal rerenders.
 */
export function createEntityFilterInput<T>(config: EntityFilterConfig<T>) {
  const cacheKey = `${config.id}:${config.labelId}`;
  const cached = entityFilterInputCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  function EntityFilterInputComponent({
    item,
    applyValue,
  }: GridFilterInputValueProps): React.ReactElement {
    const intl = useIntl();

    const data = config.useData();
    const available = useMemo(() => config.transformData(data, intl), [data, intl]);

    const { handleChange, selectedValues } = useEntityFilterInput(
      available,
      item,
      applyValue
    );

    return (
      <MemoMultiAutocomplete
        available={available}
        value={selectedValues}
        handleChange={handleChange}
        localisedLabel={formatTranslation(intl, unsafeTranslationKey(config.labelId))}
        required={false}
        error=""
        id={config.id}
        disableClearable={false}
        dense={true}
        reserveSpaceForHelperText={false}
        ref={null}
        size="small"
        maxVisibleTags={1}
        {...(config.groupBy && { groupBy: config.groupBy })}
      />
    );
  }

  EntityFilterInputComponent.displayName = `EntityFilterInput(${config.labelId})`;

  entityFilterInputCache.set(cacheKey, EntityFilterInputComponent);

  return EntityFilterInputComponent;
}
